import { motion } from 'framer-motion';
import {
  Box,
  Typography,
  Grid,
  Chip,
  useTheme,
  Avatar,
  Paper,
  styled
} from '@mui/material';
import {
  Code,
  DataObject,
  Storage,
  Security,
  BugReport,
  Build
} from '@mui/icons-material';
import React from 'react';

const SkillCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '15px',
  transition: 'transform 0.3s, box-shadow 0.3s',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[8]
  }
}));

const skillsData = [
  {
    category: 'Frontend Architecture',
    icon: <Code fontSize="large" />,
    items: ['React.js', 'TypeScript', 'Next.js', 'Vue.js', 'CSS/Tailwind'],
    color: '#2196F3'
  },
  {
    category: 'System Design',
    icon: <DataObject fontSize="large" />,
    items: ['API Design', 'Caching Strategies', 'Performance Optimization', 'WCAG Accessibility', 'Core Web Vitals'],
    color: '#4CAF50'
  },
  {
    category: 'Backend & Integration',
    icon: <Build fontSize="large" />,
    items: ['Node.js', 'Express.js', 'RESTful APIs', 'MongoDB', 'Distributed Systems'],
    color: '#FF6F00'
  },
  {
    category: 'Generative AI & LLMs',
    icon: <Code fontSize="large" />,
    items: ['Claude API', 'Google Gemini', 'GPT-5', 'Prompt Engineering', 'Multi-Model Integration'],
    color: '#9C27B0'
  },
  {
    category: 'Agentic & RAG Systems',
    icon: <Storage fontSize="large" />,
    items: ['RAG Architecture', 'ChromaDB', 'Vector Databases', 'Semantic Search', 'MCP Integration'],
    color: '#D32F2F'
  },
  {
    category: 'Testing & Quality',
    icon: <BugReport fontSize="large" />,
    items: ['Vitest', 'Jest', 'Testing Library', 'Cypress', 'TDD'],
    color: '#FF9800'
  },
  {
    category: 'DevOps & Cloud',
    icon: <Storage fontSize="large" />,
    items: ['AWS Certified', 'Docker', 'CI/CD', 'GitHub Actions', 'Jenkins'],
    color: '#1565C0'
  },
  {
    category: 'Security & Compliance',
    icon: <Security fontSize="large" />,
    items: ['JWT Authentication', 'OWASP Standards', 'CORS/CSP', 'Security Headers', 'ISC2 Certified'],
    color: '#F44336'
  },
  {
    category: 'AI-Assisted Development',
    icon: <Code fontSize="large" />,
    items: ['GitHub Copilot', 'Claude Code', 'AI Code Review', 'MCP Tools', 'LLM Workflows'],
    color: '#00838F'
  }
];

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (index: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: index * 0.1,
      duration: 0.6
    }
  })
};

const Skills = () => {
  const theme = useTheme();

  const isMobile = window.innerWidth <= 768;

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Typography
          className="minimal-title gradient-text"
          variant="h2"
          sx={{
            textAlign: 'center',
            mb: 6,
            fontWeight: 700,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            letterSpacing: '-1px',
          }}
        >
          My Skills & Tools
        </Typography>
      </motion.div>

      <Grid container spacing={4}>
        {skillsData.map((skillCategory, index) => (
          <Grid item xs={12} sm={6} md={4} key={skillCategory.category}>
            <motion.div
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              custom={index}
            >
              <SkillCard elevation={4} sx={{
                background: '#fff',
                border: '1px solid #e3f2fd',
                boxShadow: '0 2px 12px rgba(21,101,192,0.06)',
                height: 380,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                p: 3,
                transition: 'box-shadow 0.2s, border-color 0.2s',
                '&:hover': {
                  boxShadow: '0 6px 24px rgba(21,101,192,0.12)',
                  borderColor: '#2196f3',
                },
              }}>
                <motion.div whileHover={{ scale: 1.15 }} style={{ display: 'inline-block' }}>
                  <Avatar sx={{
                    bgcolor: skillCategory.color,
                    mb: 2,
                    width: 56,
                    height: 56,
                    boxShadow: '0 2px 8px rgba(21,101,192,0.10)',
                  }}>
                    {skillCategory.icon}
                  </Avatar>
                </motion.div>

                <Typography variant="h5" sx={{ fontWeight: 700, color: skillCategory.color, mb: 2, textAlign: 'center' }}>
                  {skillCategory.category}
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', flex: 1, alignContent: 'flex-start' }}>
                  {skillCategory.items.map((skill, skillIndex) => (
                    <motion.div
                      key={skill}
                      whileHover={{ scale: 1.08 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Chip
                        label={skill}
                        sx={{
                          bgcolor: '#e3f2fd',
                          color: '#1565c0',
                          fontWeight: 600,
                          fontSize: '0.95rem',
                          borderRadius: 2,
                          boxShadow: '0 1px 4px rgba(21,101,192,0.06)',
                          '&:hover': {
                            bgcolor: skillCategory.color,
                            color: '#fff',
                          },
                        }}
                      />
                    </motion.div>
                  ))}
                </Box>
              </SkillCard>
            </motion.div>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Skills;